import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { SponsorCatalogQuery } from '@/lib/contract'
import { sponsorApi, SponsorApiError } from './api'

/* Spec PWA-001 — chaves e opcoes de consulta compartilhadas por rotas (beforeLoad) e telas. */

export const queryKeys = {
    bootstrap: ['sponsor', 'bootstrap'] as const,
    me: ['sponsor', 'me'] as const,
    preferences: ['sponsor', 'me', 'preferences'] as const,
    account: ['sponsor', 'account'] as const,
    children: ['sponsor', 'children'] as const,
    child: (childRef: string) => ['sponsor', 'children', childRef] as const,
    // PWA-027
    timeline: (childRef: string) => ['sponsor', 'children', childRef, 'timeline'] as const,
    rituals: ['sponsor', 'rituals'] as const,
    notices: ['sponsor', 'notices'] as const,
    updates: ['sponsor', 'updates'] as const,
    threads: ['sponsor', 'threads'] as const,
    thread: (threadId: string) => ['sponsor', 'threads', threadId] as const,
    help: ['sponsor', 'help'] as const,
    donations: ['sponsor', 'donations'] as const,
    donationCatalog: ['sponsor', 'donation-catalog'] as const,
    savedCards: ['sponsor', 'payment-methods'] as const,
    impact: ['sponsor', 'impact'] as const,
    events: ['sponsor', 'events'] as const,
    pushConfig: ['sponsor', 'push', 'config'] as const,
    // PWA-015
    catalog: ['sponsor', 'catalog'] as const,
    catalogPage: (query: Omit<SponsorCatalogQuery, 'cursor'>) => ['sponsor', 'catalog', 'list', query] as const,
    catalogChild: (id: string) => ['sponsor', 'catalog', 'child', id] as const,
    appSponsorship: (protocol: string) => ['sponsor', 'app-sponsorship', protocol] as const,
}

// Nao repete erros de dominio (sessao, nao encontrado, indisponivel): so falha de rede.
const retryNetworkOnly = (failureCount: number, error: unknown) =>
    error instanceof SponsorApiError && error.code === 'network' && failureCount < 2

const base = { retry: retryNetworkOnly, staleTime: 60_000 }
// Dados de crianca e midia temporaria: sem cache prolongado no cliente (FR-016, FR-022).
const sensitive = { ...base, gcTime: 5 * 60_000 }

export const bootstrapQuery = queryOptions({ queryKey: queryKeys.bootstrap, queryFn: sponsorApi.bootstrap, ...base, staleTime: 5 * 60_000 })
export const meQuery = queryOptions({ queryKey: queryKeys.me, queryFn: sponsorApi.me, ...base })
export const preferencesQuery = queryOptions({ queryKey: queryKeys.preferences, queryFn: sponsorApi.preferences, ...base })
// PWA-014: chave publica dos avisos (muda so com um par novo no servidor)
export const pushConfigQuery = queryOptions({ queryKey: queryKeys.pushConfig, queryFn: sponsorApi.pushConfig, ...base, staleTime: 10 * 60_000 })
export const accountQuery = queryOptions({ queryKey: queryKeys.account, queryFn: sponsorApi.account, ...base })
export const childrenQuery = queryOptions({ queryKey: queryKeys.children, queryFn: sponsorApi.children, ...sensitive })
export const childQuery = (childRef: string) =>
    queryOptions({ queryKey: queryKeys.child(childRef), queryFn: () => sponsorApi.child(childRef), ...sensitive })
export const noticesQuery = queryOptions({ queryKey: queryKeys.notices, queryFn: sponsorApi.notices, ...base, staleTime: 30_000 })
export const threadsQuery = queryOptions({ queryKey: queryKeys.threads, queryFn: sponsorApi.threads, ...sensitive, staleTime: 30_000 })
export const threadQuery = (threadId: string) =>
    // PWA-011: links das fotos/audios valem 15 min; a conversa aberta se renova antes disso
    queryOptions({ queryKey: queryKeys.thread(threadId), queryFn: () => sponsorApi.thread(threadId), ...sensitive, refetchInterval: 10 * 60_000 })
export const helpQuery = queryOptions({ queryKey: queryKeys.help, queryFn: sponsorApi.help, ...base, staleTime: 10 * 60_000 })

export const donationsQuery = queryOptions({ queryKey: queryKeys.donations, queryFn: sponsorApi.donations, ...base, staleTime: 30_000 })
export const donationCatalogQuery = queryOptions({ queryKey: queryKeys.donationCatalog, queryFn: sponsorApi.donationCatalog, ...base, staleTime: 10 * 60_000 })
export const savedCardsQuery = queryOptions({ queryKey: queryKeys.savedCards, queryFn: sponsorApi.savedCards, ...base })
export const impactQuery = queryOptions({ queryKey: queryKeys.impact, queryFn: sponsorApi.impact, ...base, staleTime: 10 * 60_000 })
export const eventsQuery = queryOptions({ queryKey: queryKeys.events, queryFn: sponsorApi.events, ...base, staleTime: 5 * 60_000 })


// PWA-017: Novidades do Inicio — tudo o que chegou, 5 por pagina ("Ver mais")
export const updatesQuery = infiniteQueryOptions({
    queryKey: queryKeys.updates,
    queryFn: ({ pageParam }) => sponsorApi.updates(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    ...sensitive,
    staleTime: 30_000,
})

// PWA-027: linha do tempo de uma crianca (20 por pagina, "Ver mais") e rituais ativos hoje (cartoes da Inicio)
export const timelineQuery = (childRef: string) =>
    infiniteQueryOptions({
        queryKey: queryKeys.timeline(childRef),
        queryFn: ({ pageParam }) => sponsorApi.timeline(childRef, pageParam),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.next ?? undefined,
        ...sensitive,
        staleTime: 30_000,
    })
export const ritualsQuery = queryOptions({ queryKey: queryKeys.rituals, queryFn: sponsorApi.rituals, ...base, staleTime: 10 * 60_000 })

// PWA-015: catalogo de criancas aguardando padrinho (paginas por cursor) e a ficha de cada uma (sem cache prolongado)
export const catalogQuery = (query: Omit<SponsorCatalogQuery, 'cursor'>) =>
    infiniteQueryOptions({
        queryKey: queryKeys.catalogPage(query),
        queryFn: ({ pageParam }) => sponsorApi.catalog({ ...query, cursor: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.next ?? undefined,
        ...sensitive,
        staleTime: 30_000,
    })
// PWA-019: apadrinhamento feito pelo app (situacao, cobrancas, alteracoes)
export const appSponsorshipQuery = (protocol: string) =>
    queryOptions({ queryKey: queryKeys.appSponsorship(protocol), queryFn: () => sponsorApi.appSponsorship(protocol), ...base, staleTime: 15_000 })
/** PWA-022: qual provedor de cartao o aparelho mostra. */
export const paymentsConfigQuery = () =>
    queryOptions({ queryKey: ['sponsor', 'payments', 'config'] as const, queryFn: () => sponsorApi.paymentsConfig(), staleTime: 5 * 60_000 })

export const catalogChildQuery = (id: string) =>
    queryOptions({ queryKey: queryKeys.catalogChild(id), queryFn: () => sponsorApi.catalogChild(id), ...sensitive, staleTime: 15_000 })
